import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface HeaderProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  logout: () => void;
}

interface ValuationUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  role: string;
}

export default function Header({ theme, setTheme, logout }: HeaderProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<ValuationUser | null>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [theme]);

  /* ================= LOAD USER ================= */
  useEffect(() => {
    const storedUser = localStorage.getItem("valuation_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Failed to parse valuation_user", error);
      }
    }
  }, []);

  /* ================= ACTIVE LINK ================= */
  const isActive = (href: string) => {
    if (href === "/supervisor-dashboard") {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  /* ================= OUTSIDE CLICK ================= */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (profileMenuOpen || mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen, mobileMenuOpen]);

  const navLinks = [
    { href: "/supervisor-dashboard", label: "Dashboard" },
    { href: "/supervisor-dashboard/view-data", label: "Data" },
    { href: "/supervisor-dashboard/view-report", label: "Reports" },

  ];

  return (
    <header className="z-50 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* LOGO */}
          <Link to="/supervisor-dashboard" className="flex items-center space-x-2 flex-shrink-0">
            <img src="/logo.png" alt="Logo" className={`h-12 w-16 mt-1 ${theme === "dark" ? "dark:bg-gray-400" : ""}`} />
            <span className="hidden sm:block text-sm text-gray-600 dark:text-white">
              Real-time Property Valuation
            </span>
            {/* <span className="sm:hidden text-lg font-semibold text-gray-900 dark:text-white">
              RPV
            </span> */}
          </Link>

          {/* RIGHT SIDE */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* THEME TOGGLE */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>

            {/* DESKTOP PROFILE MENU */}
            <div className="hidden sm:block relative" ref={profileRef}>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center text-sm rounded-md px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
              >
                {user?.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt="Profile"
                    className="h-6 w-6 rounded-full mr-2"
                  />
                ) : (
                  <span className="mr-2">👤</span>
                )}
                <span className="hidden md:inline">
                  {user ? `${user.firstName} ${user.lastName}` : "Account"}
                </span>
                <span className="ml-1">▼</span>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  {["Profile Settings", "Account Security", "Preferences"].map(
                    (item) => (
                      <div
                        key={item}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        {item}
                      </div>
                    )
                  )}
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>
                  <div
                    onClick={() => {
                      logout();
                      window.location.href = "/login";
                    }}
                    className="px-4 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Logout
                  </div>
                </div>
              )}
            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="lg:hidden border-t border-gray-200 dark:border-gray-700 py-2"
          >
            <nav className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    isActive(link.href)
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* MOBILE PROFILE SECTION */}
            <div className="sm:hidden border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
              <div className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white flex items-center">
                {user?.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt="Profile"
                    className="h-8 w-8 rounded-full mr-2"
                  />
                ) : (
                  <span className="mr-2 text-2xl">👤</span>
                )}
                <span>
                  {user ? `${user.firstName} ${user.lastName}` : "Account"}
                </span>
              </div>
              {["Profile Settings", "Account Security", "Preferences"].map(
                (item) => (
                  <div
                    key={item}
                    className="px-3 py-2 text-sm cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    {item}
                  </div>
                )
              )}
              <div
                onClick={() => {
                  logout();
                  window.location.href = "/login";
                }}
                className="px-3 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Logout
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}